import { formatDate } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { map, mergeMap, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';

import {
  CODCollectedCleared,
  CODCollectedReportResponse,
} from 'src/app/core/models/reports/reports.model';
import { CodsReportsService } from 'src/app/core/services/reports/cod/cod-reports.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { ReportsUtils } from 'src/app/core/utils/reports/reports.utils';
import { Options } from '../../../shared/report-format/report-format.constants';
import {
  CollectedReportFormGroup,
  CONSTS,
  FILE_NAME,
  PDF_REPORT,
} from './collected.constants';

@Component({
  selector: 'app-collected',
  templateUrl: './collected.component.html',
  styleUrls: ['./collected.component.scss'],
  standalone: false,
})
export class CollectedComponent implements OnInit {
  readonly destroy = inject(DestroyRef);
  readonly input: CollectedReportFormGroup = inject(MAT_DIALOG_DATA);
  private userService = inject(UserService);
  private dialogRef = inject(MatDialogRef<CollectedComponent>);
  private fb = inject(FormBuilder);
  private codsReportsService = inject(CodsReportsService);
  private commonUtils = inject(CommonUtils);
  private reportUtils = inject(ReportsUtils);

  readonly CONSTS = CONSTS;
  readonly SRC = IMAGE_SRC;
  readonly PDF_REPORT = PDF_REPORT;
  readonly BUTTON_TYPES = BUTTON_TYPES;

  collectedReportForm!: FormGroup;

  currDate!: Date;
  minDate!: Date;
  maxDate!: Date;
  cODCollectedReport!: CODCollectedReportResponse;

  @ViewChild('pdfContainer', { read: ViewContainerRef })
  pdfContainer!: ViewContainerRef;

  ngOnInit() {
    this.currDate = new Date();
    this.minDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() - 30,
    );
    this.maxDate = new Date(
      this.currDate.getFullYear(),
      this.currDate.getMonth(),
      this.currDate.getDate() + 14,
    );
    this.collectedReportForm = this.fb.nonNullable.group({
      fromDate: [this.currDate],
      toDate: [this.currDate],
      reportFormat: [Options.PDF],
    });
  }

  radioChange(newValue: Options) {
    this.collectedReportForm.get('reportFormat')?.setValue(newValue);
  }

  clickExit() {
    this.commonUtils.openExitModal(this.collectedReportForm.dirty);
  }

  /**
   * Used to get report in CSV or PDF file format
   *
   * @memberof CollectedComponent
   */
  getReport() {
    this.userService
      .getCurrentUser$()
      .pipe(
        take(1),
        mergeMap((user) => {
          const formValue = this.collectedReportForm.value;
          const locationcode = user?.location;
          const fromdate = this.commonUtils.getFormattedDate(
            formValue.fromDate,
          );
          const todate = this.commonUtils.getFormattedDate(formValue.toDate);

          const request = {
            locationcode,
            fromdate,
            todate,
          };

          return this.codsReportsService.getCODCollectedReport(request).pipe(
            take(1),
            map((data) => {
              const formatType =
                this.collectedReportForm.get('reportFormat')?.value;
              const CODCollectedCleared = data.response.CODCollectedCleared;
              if (CODCollectedCleared?.length) {
                if (formatType === Options.CSV) {
                  const customHeader = [
                    [
                      '',
                      '',
                      '',
                      '',
                      `${'COD Collected/Cleared'}`,
                      '',
                      '',
                      '',
                      '',
                      '',
                    ],
                    [
                      '',
                      '',
                      'Location:',
                      '',
                      `${data.response.location}`,
                      '',
                      '',
                      'Turn in ',
                      '',
                      `${data.response.turninDate}`,
                    ],
                  ];
                  const formatedDate = formatDate(
                    new Date(),
                    'MM-dd-yyyy',
                    'en-US',
                  );

                  const CSV_FILE_COLUMN_HEADING = [
                    'Driver'.toUpperCase(),
                    'Loop'.toUpperCase(),
                    'Trackno'.toUpperCase(),
                    'Customer'.toUpperCase(),
                    'City'.toUpperCase(),
                    'Expected'.toUpperCase(),
                    'CA/CH/C',
                    'D/P',
                    'TurnedIn'.toUpperCase(),
                    'Comments'.toUpperCase(),
                    'Collected/Cleared Date'.toUpperCase(),
                    'Delivery Date'.toUpperCase(),
                    'LocationCode'.toUpperCase(),
                  ];
                  const projectedRows = CODCollectedCleared.map(
                    (row: CODCollectedCleared) => ({
                      Driver: row.driver ?? '',
                      Loop: row.loop ?? '',
                      Trackno: row.trackno ?? '',
                      Customer: row.customer ?? '',
                      City: row.city ?? '',
                      Expected: row.expected ?? '',
                      'CA/CH/C': row['ca/ch/cc'] ?? '',
                      'D/P': row['d/p'] ?? '',
                      TurnedIn: row.turnedin ?? '',
                      Comments: row.comments ?? '',
                      'Collected/Cleared Date': row.codturnindate ?? '',
                      'Delivery Date': row.date ?? '',
                      LocationCode: row.locationcode ?? '',
                    }),
                  );
                  const customFooter = [
                    [],
                    [
                      'Total Expected ',
                      '',
                      `${data?.response.TotalExpectedCOD}`,
                      '',
                      '',
                      '',
                      'Total Turned In',
                      '',
                      `${data?.response.TotalTurnedInCOD}`,
                      '',
                      'Turn In',
                      `${data?.response.turninDate}`,
                    ],
                    ['Printed:', `${formatedDate}`],
                  ];
                  this.reportUtils.exportCSVReport(
                    projectedRows,
                    FILE_NAME,
                    customHeader,
                    customFooter,
                    CSV_FILE_COLUMN_HEADING,
                  );
                }
                if (formatType === Options.PDF) {
                  this.cODCollectedReport = data;
                  setTimeout(() => {
                    const content =
                      this.pdfContainer.element.nativeElement.innerHTML;
                    this.reportUtils.exportPdfReport(
                      content,
                      PDF_REPORT.NAME,
                      FILE_NAME,
                    );
                  }, 0);
                }
              } else {
                this.commonUtils.updateBannerType(
                  BANNER_TYPE.NO_DATA_AVAILABLE,
                );
              }
              this.dialogRef.close();
            }),
          );
        }),
      )
      .subscribe();
  }
}
